import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { AlertCircle, Clock, ShieldCheck } from 'lucide-react';

type StatusContext = 'domain' | 'invoice';

type StatusStyle = {
  label: string;
  className?: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  icon?: React.ComponentType<{ className?: string }>;
  tooltip?: string;
};

const DOMAIN_STATUS_CONFIG: Record<string, StatusStyle> = {
  active: {
    label: 'Protected',
    className: 'gap-1 cursor-pointer',
    variant: 'success',
    icon: ShieldCheck,
    tooltip: 'Your site is actively monitored and protected by ShadowScan.',
  },
  pending: {
    label: 'Pending Setup',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: Clock,
    tooltip: 'Our team is setting up security monitoring for your site.',
  },
  payment_needed: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  payment_incomplete: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  pending_payment: {
    label: 'Billing paused',
    className: 'gap-1 cursor-pointer',
    variant: 'warning',
    icon: AlertCircle,
    tooltip: 'Protection remains active. If billing is not resumed within 7 days, automated updates and configuration changes are temporarily paused until billing resumes.',
  },
  deleted: {
    label: 'Removed',
    className: 'gap-1 cursor-pointer',
    variant: 'danger',
    icon: AlertCircle,
    tooltip: 'This site has been removed and can be restored by an administrator if needed.',
  },
};

const INVOICE_STATUS_CONFIG: Record<string, StatusStyle> = {
  paid: {
    label: 'Paid',
    variant: 'success',
  },
  open: {
    label: 'Open',
    variant: 'warning',
  },
  draft: {
    label: 'Draft',
    variant: 'neutral',
  },
  void: {
    label: 'Void',
    variant: 'danger',
  },
};

const formatLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

type StatusBadgeProps = {
  status: string;
  context?: StatusContext;
  className?: string;
  tooltip?: string;
};

export function StatusBadge({
  status,
  context = 'domain',
  className,
  tooltip,
}: StatusBadgeProps) {
  const normalized = status?.toLowerCase?.() ?? '';
  const config =
    (context === 'invoice' ? INVOICE_STATUS_CONFIG[normalized] : DOMAIN_STATUS_CONFIG[normalized]) ??
    ({
      label: formatLabel(status || 'Unknown'),
      variant: 'neutral',
      tooltip: status ? `Status: ${formatLabel(status)}` : undefined,
    } satisfies StatusStyle);

  const badgeContent = (
    <Badge
      variant={config.variant}
      className={cn('flex items-center', config.className, className)}
    >
      {config.icon && <config.icon className="h-3 w-3" />}
      {config.label}
    </Badge>
  );

  const tooltipContent = tooltip || config.tooltip;

  if (!tooltipContent) {
    return badgeContent;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{badgeContent}</PopoverTrigger>
      <PopoverContent className="w-fit max-w-xs px-3 py-2 text-sm">
        {tooltipContent}
      </PopoverContent>
    </Popover>
  );
}
